import { useState, useMemo } from "react";
import { OrgPicker } from "@/components/OrgPicker";
import { useQuery } from "@tanstack/react-query";
import {
	User,
	Building2,
	Shield,
	Package,
	Eye,
	EyeOff,
	Check,
	X,
	Search,
	ChevronDown,
	ChevronRight,
	Menu,
	LayoutDashboard,
	Settings,
	Users,
	FileText,
	BarChart3,
	Activity,
	CreditCard,
	RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import * as api from "@/services/api";

// Icon mapping
const iconMap: Record<string, React.ElementType> = {
	dashboard: LayoutDashboard,
	settings: Settings,
	users: Users,
	"file-text": FileText,
	shield: Shield,
	"bar-chart": BarChart3,
	package: Package,
	building: Building2,
	"credit-card": CreditCard,
	activity: Activity,
	menu: Menu,
};

export function UserPerspective() {
	const [selectedOrgId, setSelectedOrgId] = useState<string>("");
	const [selectedUserId, setSelectedUserId] = useState<string>("");
	const [permissionSearch, setPermissionSearch] = useState("");
	const [selectedModule, setSelectedModule] = useState<string | null>(null);
	const [expandedSections, setExpandedSections] = useState<Set<string>>(
		new Set(["Overview", "Management", "System"])
	);

	// Get organizations
	const { data: orgs } = useQuery({
		queryKey: ["organizations"],
		queryFn: api.getOrganizations,
	});

	// Get users for selected org
	const { data: orgUsers } = useQuery({
		queryKey: ["org-users", selectedOrgId],
		queryFn: () => api.getOrganizationUsers(selectedOrgId),
		enabled: !!selectedOrgId,
	});

	// Get user perspective
	const {
		data: perspective,
		isLoading: perspectiveLoading,
		refetch: refetchPerspective,
	} = useQuery({
		queryKey: ["user-perspective", selectedUserId],
		queryFn: () => api.getUserPerspective(selectedUserId),
		enabled: !!selectedUserId,
	});

	// Get modules from permissions
	const modules = useMemo(() => {
		if (!perspective) return [];
		return Object.keys(perspective.permissionsByModule).sort();
	}, [perspective]);

	// Filter permissions
	const filteredPermissions = useMemo(() => {
		if (!perspective) return [];
		let perms = perspective.permissions;

		if (permissionSearch) {
			const search = permissionSearch.toLowerCase();
			perms = perms.filter(
				(p) =>
					p.feature.toLowerCase().includes(search) ||
					p.featureName.toLowerCase().includes(search)
			);
		}

		if (selectedModule) {
			perms = perms.filter((p) => p.module === selectedModule);
		}

		return perms;
	}, [perspective, permissionSearch, selectedModule]);

	const toggleSection = (section: string) => {
		setExpandedSections((prev) => {
			const next = new Set(prev);
			if (next.has(section)) {
				next.delete(section);
			} else {
				next.add(section);
			}
			return next;
		});
	};

	const getIcon = (iconName?: string) => {
		if (!iconName) return Menu;
		return iconMap[iconName.toLowerCase()] || Menu;
	};

	// Menu Preview Component
	const MenuPreview = () => {
		if (!perspective) return null;

		const renderMenuItem = (
			item: (typeof perspective.menu.allItems)[0],
			depth = 0
		) => {
			const IconComponent = getIcon(item.icon);
			const hasChildren = item.children && item.children.length > 0;
			const isExpanded = expandedSections.has(item.code);

			return (
				<div key={item.code}>
					<div
						className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
							item.visible
								? "text-gray-300 hover:bg-gray-800 hover:text-white cursor-pointer"
								: "text-gray-600 line-through opacity-50"
						}`}
						style={{ paddingLeft: `${12 + depth * 16}px` }}
						onClick={() => hasChildren && toggleSection(item.code)}
					>
						<IconComponent className="w-4 h-4" />
						<span className="font-medium flex-1">{item.label}</span>
						{!item.visible && (
							<Badge variant="destructive" className="text-[10px] px-1">
								{item.reason?.replace(/_/g, " ")}
							</Badge>
						)}
						{hasChildren &&
							(isExpanded ? (
								<ChevronDown className="w-4 h-4" />
							) : (
								<ChevronRight className="w-4 h-4" />
							))}
					</div>
					{hasChildren && isExpanded && (
						<div className="mt-1">
							{item.children!.map((child: any) => renderMenuItem(child, depth + 1))}
						</div>
					)}
				</div>
			);
		};

		return (
			<div className="w-72 bg-gray-900 text-white rounded-lg overflow-hidden shadow-xl">
				<div className="p-4 border-b border-gray-800">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center font-semibold">
							{perspective.user.firstName?.[0] || perspective.user.email[0].toUpperCase()}
						</div>
						<div className="flex-1 min-w-0">
							<p className="text-sm font-medium truncate">
								{perspective.user.firstName} {perspective.user.lastName}
							</p>
							<p className="text-xs text-gray-400 truncate">
								{perspective.user.email}
							</p>
						</div>
					</div>
				</div>
				<nav className="p-3 space-y-4 max-h-[500px] overflow-y-auto">
					{Object.entries(perspective.menu.bySection).map(([section, items]) => (
						<div key={section}>
							<button
								type="button"
								onClick={() => toggleSection(section)}
								className="w-full flex items-center justify-between px-2 py-1 text-xs text-gray-500 uppercase font-semibold hover:text-gray-400"
							>
								{section === "_root" ? "Other" : section}
								{expandedSections.has(section) ? (
									<ChevronDown className="w-3 h-3" />
								) : (
									<ChevronRight className="w-3 h-3" />
								)}
							</button>
							{expandedSections.has(section) && (
								<div className="mt-1 space-y-0.5">
									{items.map((item) => renderMenuItem(item))}
								</div>
							)}
						</div>
					))}
				</nav>
			</div>
		);
	};

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">
						User Perspective Simulator
					</h1>
					<p className="text-gray-500 mt-1">
						Debug what a specific user sees - their permissions, features, and menu
					</p>
				</div>
				{perspective && (
					<Button
						variant="outline"
						onClick={() => refetchPerspective()}
						disabled={perspectiveLoading}
					>
						<RefreshCw
							className={`w-4 h-4 mr-2 ${perspectiveLoading ? "animate-spin" : ""}`}
						/>
						Refresh
					</Button>
				)}
			</div>

			{/* User Selection */}
			<Card>
				<CardHeader className="pb-3">
					<CardTitle className="text-lg flex items-center gap-2">
						<User className="w-5 h-5" />
						Select User to Simulate
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex gap-4">
						<div className="flex-1">
							<label className="text-sm font-medium text-gray-700 mb-1 block">
								Organization
							</label>
							<OrgPicker
								orgs={orgs}
								value={selectedOrgId}
								onChange={(v) => {
									setSelectedOrgId(v);
									setSelectedUserId("");
								}}
							/>
						</div>
						<div className="flex-1">
							<label className="text-sm font-medium text-gray-700 mb-1 block">
								User
							</label>
							<Select
								value={selectedUserId}
								onValueChange={setSelectedUserId}
								disabled={!selectedOrgId}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select user..." />
								</SelectTrigger>
								<SelectContent>
									{orgUsers?.users?.map((user) => (
										<SelectItem key={user._id} value={user._id}>
											<span className="flex items-center gap-2">
												<User className="w-4 h-4" />
												{user.name || user.email}
												<span className="text-gray-400 text-xs">
													({user.email})
												</span>
											</span>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				</CardContent>
			</Card>

			{perspectiveLoading && (
				<div className="flex items-center justify-center h-64">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
				</div>
			)}

			{perspective && !perspectiveLoading && (
				<>
					{/* Summary Stats */}
					<div className="grid grid-cols-4 gap-4">
						<Card>
							<CardContent className="pt-4">
								<div className="flex items-center gap-3">
									<div className="p-2 rounded-lg bg-blue-100">
										<Package className="w-5 h-5 text-blue-600" />
									</div>
									<div>
										<div className="text-2xl font-bold">
											{perspective.summary.effectiveFeatures}
										</div>
										<p className="text-xs text-muted-foreground">
											Effective Features
										</p>
									</div>
								</div>
							</CardContent>
						</Card>
						<Card>
							<CardContent className="pt-4">
								<div className="flex items-center gap-3">
									<div className="p-2 rounded-lg bg-green-100">
										<Eye className="w-5 h-5 text-green-600" />
									</div>
									<div>
										<div className="text-2xl font-bold">
											{perspective.summary.featuresWithView}
										</div>
										<p className="text-xs text-muted-foreground">
											Can View
										</p>
									</div>
								</div>
							</CardContent>
						</Card>
						<Card>
							<CardContent className="pt-4">
								<div className="flex items-center gap-3">
									<div className="p-2 rounded-lg bg-purple-100">
										<Menu className="w-5 h-5 text-purple-600" />
									</div>
									<div>
										<div className="text-2xl font-bold">
											{perspective.summary.visibleMenuItems}
										</div>
										<p className="text-xs text-muted-foreground">
											Visible Menu Items
										</p>
									</div>
								</div>
							</CardContent>
						</Card>
						<Card>
							<CardContent className="pt-4">
								<div className="flex items-center gap-3">
									<div className="p-2 rounded-lg bg-red-100">
										<EyeOff className="w-5 h-5 text-red-600" />
									</div>
									<div>
										<div className="text-2xl font-bold">
											{perspective.summary.hiddenMenuItems}
										</div>
										<p className="text-xs text-muted-foreground">
											Hidden Menu Items
										</p>
									</div>
								</div>
							</CardContent>
						</Card>
					</div>

					{/* Main Content */}
					<div className="flex gap-6">
						{/* Left Panel - Details */}
						<div className="flex-1">
							<Tabs defaultValue="permissions" className="space-y-4">
								<TabsList>
									<TabsTrigger value="permissions">Permissions</TabsTrigger>
									<TabsTrigger value="roles">Roles</TabsTrigger>
									<TabsTrigger value="plan">Plan & Features</TabsTrigger>
									<TabsTrigger value="menu">Menu Items</TabsTrigger>
								</TabsList>

								{/* Permissions Tab */}
								<TabsContent value="permissions" className="space-y-4">
									<Card>
										<CardHeader className="pb-3">
											<div className="flex items-center justify-between">
												<CardTitle className="text-lg">
													Feature Permissions
												</CardTitle>
												<div className="flex gap-2">
													<div className="relative">
														<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
														<Input
															placeholder="Search features..."
															value={permissionSearch}
															onChange={(e) =>
																setPermissionSearch(e.target.value)
															}
															className="pl-10 w-64"
														/>
													</div>
													<Select
														value={selectedModule || "all"}
														onValueChange={(v) =>
															setSelectedModule(v === "all" ? null : v)
														}
													>
														<SelectTrigger className="w-40">
															<SelectValue placeholder="All modules" />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="all">All modules</SelectItem>
															{modules.map((m) => (
																<SelectItem key={m} value={m}>
																	{m}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
											</div>
										</CardHeader>
										<CardContent>
											<div className="space-y-2 max-h-[500px] overflow-y-auto">
												{filteredPermissions.map((perm) => (
													<div
														key={perm.feature}
														className="flex items-center gap-4 p-3 rounded-lg border hover:bg-gray-50"
													>
														<div className="flex-1 min-w-0">
															<div className="flex items-center gap-2">
																<span className="font-medium">
																	{perm.featureName}
																</span>
																<code className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
																	{perm.feature}
																</code>
															</div>
															<p className="text-xs text-gray-500">
																Module: {perm.module}
															</p>
														</div>
														<div className="flex gap-1">
															{Object.entries(perm.actions).map(
																([action, allowed]) => (
																	<Badge
																		key={action}
																		variant={allowed ? "default" : "outline"}
																		className={`text-[10px] ${
																			allowed
																				? "bg-green-100 text-green-700 hover:bg-green-100"
																				: "text-gray-400"
																		}`}
																	>
																		{allowed ? (
																			<Check className="w-3 h-3 mr-1" />
																		) : (
																			<X className="w-3 h-3 mr-1" />
																		)}
																		{action.charAt(0).toUpperCase()}
																	</Badge>
																)
															)}
														</div>
													</div>
												))}
												{filteredPermissions.length === 0 && (
													<p className="text-center py-8 text-gray-500">
														No permissions found
													</p>
												)}
											</div>
										</CardContent>
									</Card>
								</TabsContent>

								{/* Roles Tab */}
								<TabsContent value="roles" className="space-y-4">
									<div className="grid grid-cols-2 gap-4">
										<Card>
											<CardHeader className="pb-3">
												<CardTitle className="text-lg flex items-center gap-2">
													<Shield className="w-5 h-5" />
													MongoDB Roles
												</CardTitle>
											</CardHeader>
											<CardContent>
												{perspective.roles.mongodb.length > 0 ? (
													<div className="space-y-2">
														{perspective.roles.mongodb.map((role) => (
															<div
																key={role._id}
																className="p-3 rounded-lg border"
															>
																<div className="flex items-center justify-between">
																	<span className="font-medium">
																		{role.displayName || role.name}
																	</span>
																	<div className="flex gap-1">
																		{role.isGlobal && (
																			<Badge variant="outline">Global</Badge>
																		)}
																		{role.dataAccessScope && (
																			<Badge variant="secondary">
																				{role.dataAccessScope}
																			</Badge>
																		)}
																	</div>
																</div>
																<code className="text-xs text-gray-400">
																	{role.name}
																</code>
															</div>
														))}
													</div>
												) : (
													<p className="text-gray-500 text-center py-4">
														No roles assigned
													</p>
												)}
											</CardContent>
										</Card>

										</div>
								</TabsContent>

								{/* Plan Tab */}
								<TabsContent value="plan" className="space-y-4">
									<div className="grid grid-cols-2 gap-4">
										<Card>
											<CardHeader className="pb-3">
												<CardTitle className="text-lg flex items-center gap-2">
													<Package className="w-5 h-5" />
													Plan: {perspective.plan.name}
												</CardTitle>
											</CardHeader>
											<CardContent>
												<p className="text-sm text-gray-500 mb-3">
													{perspective.plan.featuresCount} features included
												</p>
												<div className="flex flex-wrap gap-1 max-h-[200px] overflow-y-auto">
													{perspective.plan.features.map((f) => (
														<Badge
															key={f}
															variant="outline"
															className="text-xs"
														>
															{f}
														</Badge>
													))}
												</div>
											</CardContent>
										</Card>

										<Card>
											<CardHeader className="pb-3">
												<CardTitle className="text-lg flex items-center gap-2">
													<Settings className="w-5 h-5" />
													Custom Package
												</CardTitle>
											</CardHeader>
											<CardContent>
												{perspective.organization.customPackage.enabled ? (
													<div className="space-y-3">
														<div className="flex items-center gap-2">
															<Badge variant="default">Enabled</Badge>
														</div>
														<div>
															<p className="text-sm font-medium">
																Added Features:{" "}
																{
																	perspective.organization.customPackage
																		.customFeaturesCount
																}
															</p>
															<p className="text-sm font-medium text-red-600">
																Blocked Features:{" "}
																{
																	perspective.organization.customPackage
																		.blockedFeaturesCount
																}
															</p>
														</div>
														{perspective.blockedFeatures.length > 0 && (
															<div>
																<p className="text-xs text-gray-500 mb-1">
																	Blocked:
																</p>
																<div className="flex flex-wrap gap-1">
																	{perspective.blockedFeatures.map((f) => (
																		<Badge
																			key={f}
																			variant="destructive"
																			className="text-xs"
																		>
																			{f}
																		</Badge>
																	))}
																</div>
															</div>
														)}
													</div>
												) : (
													<p className="text-gray-500">
														No custom package configured
													</p>
												)}
											</CardContent>
										</Card>
									</div>

									<Card>
										<CardHeader className="pb-3">
											<CardTitle className="text-lg">
												Effective Features ({perspective.effectiveFeatures.length})
											</CardTitle>
										</CardHeader>
										<CardContent>
											<div className="flex flex-wrap gap-1 max-h-[200px] overflow-y-auto">
												{perspective.effectiveFeatures.map((f) => (
													<Badge key={f} variant="secondary" className="text-xs">
														{f}
													</Badge>
												))}
											</div>
										</CardContent>
									</Card>
								</TabsContent>

								{/* Menu Tab */}
								<TabsContent value="menu" className="space-y-4">
									<Card>
										<CardHeader className="pb-3">
											<CardTitle className="text-lg">
												All Menu Items (
												{perspective.menu.allItems.filter((m) => m.visible).length}{" "}
												visible,{" "}
												{perspective.menu.allItems.filter((m) => !m.visible).length}{" "}
												hidden)
											</CardTitle>
										</CardHeader>
										<CardContent>
											<div className="space-y-2 max-h-[400px] overflow-y-auto">
												{perspective.menu.allItems.map((item) => (
													<div
														key={item.code}
														className={`flex items-center gap-3 p-3 rounded-lg border ${
															item.visible
																? "bg-white"
																: "bg-gray-50 opacity-60"
														}`}
													>
														{item.visible ? (
															<Eye className="w-4 h-4 text-green-600" />
														) : (
															<EyeOff className="w-4 h-4 text-red-600" />
														)}
														<div className="flex-1">
															<span className="font-medium">{item.label}</span>
															<div className="flex gap-2 mt-1">
																<code className="text-xs text-gray-400 bg-gray-100 px-1 rounded">
																	{item.code}
																</code>
																{item.route && (
																	<span className="text-xs text-blue-500">
																		{item.route}
																	</span>
																)}
															</div>
														</div>
														{!item.visible && item.reason && (
															<Badge variant="destructive" className="text-xs">
																{item.reason.replace(/_/g, " ")}
															</Badge>
														)}
														<Badge variant="outline" className="text-xs">
															Order: {item.order}
														</Badge>
													</div>
												))}
											</div>
										</CardContent>
									</Card>
								</TabsContent>
							</Tabs>
						</div>

						{/* Right Panel - Menu Preview */}
						<div className="sticky top-4 self-start">
							<h3 className="text-sm font-medium text-gray-500 mb-2">
								Menu Preview (As User Sees It)
							</h3>
							<MenuPreview />
							<p className="text-xs text-gray-400 mt-2 text-center">
								Generated: {new Date(perspective.generatedAt).toLocaleString()}
							</p>
						</div>
					</div>
				</>
			)}

			{!selectedUserId && !perspectiveLoading && (
				<Card>
					<CardContent className="py-12">
						<div className="text-center text-gray-500">
							<User className="w-12 h-12 mx-auto mb-4 opacity-50" />
							<p className="text-lg font-medium">Select a User to Simulate</p>
							<p className="text-sm mt-1">
								Choose an organization and user above to see their perspective
							</p>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
